import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { KnowledgeView, QualityGrade } from "@cloudox/api-client";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { QualityBadge } from "./QualityBadge";

/**
 * A Knowledge View card for the Workspace Overview grid. Communicates what the
 * view answers (its lead persona question), how confident the knowledge is, and
 * its quality grade — so the reader chooses a lens by intent, not by file name.
 */
export interface ViewCardProps {
  readonly view: KnowledgeView;
  readonly href: string;
  /**
   * `undefined` when no quality artifact was loaded (no badge), `null` when it
   * was and this view carries no score (a "Not scored" badge). The two are
   * different facts and the card renders them differently.
   */
  readonly grade?: QualityGrade | null;
  readonly score?: number | null;
  /** Why this view has no score; shown as the badge tooltip. */
  readonly unscoredReason?: string;
  readonly icon?: ReactNode;
  readonly locked?: boolean;
}

export function ViewCard({
  view,
  href,
  grade,
  score,
  unscoredReason,
  icon,
  locked = false,
}: ViewCardProps) {
  const lead = view.personaQuestions[0];
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {icon ? (
            <span aria-hidden style={{ color: "var(--accent)" }}>
              {icon}
            </span>
          ) : null}
          <h3
            className="truncate text-sm font-semibold"
            style={{ color: "var(--fg)" }}
          >
            {view.displayName}
          </h3>
        </div>
        {grade !== undefined ? (
          <QualityBadge
            grade={grade}
            score={score}
            unscoredReason={unscoredReason}
            size="sm"
          />
        ) : null}
      </div>
      {lead ? (
        <p
          className="mt-2 line-clamp-2 text-sm"
          style={{ color: "var(--fg-muted)" }}
        >
          {lead}
        </p>
      ) : view.audience ? (
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          {view.audience}
        </p>
      ) : null}
      <div className="mt-3 flex items-center justify-between">
        {/* Labelled, not the compact dot used in dense rows: a card has the
            room, and confidence is a claim about the knowledge. Encoding it in
            hue alone leaves a sighted reader to guess and everyone with a
            colour-vision deficiency with nothing. */}
        <ConfidenceBadge confidence={view.overallConfidence} />
        <span className="text-xs" style={{ color: "var(--fg-subtle)" }}>
          {view.sectionCount} section{view.sectionCount === 1 ? "" : "s"}
        </span>
      </div>
    </>
  );

  if (locked) {
    return (
      <div
        className="card"
        style={{ opacity: 0.6 }}
        aria-disabled
        title="Upgrade to unlock this view"
      >
        {body}
      </div>
    );
  }
  return (
    <Link
      to={href}
      className="card transition hover:border-[color:var(--accent)]"
      style={{ textDecoration: "none" }}
    >
      {body}
    </Link>
  );
}
